import { format } from 'date-fns';
import linkifyHtml from 'linkify-html';
import { upperFirst } from 'lodash';
import { formatPrice } from '../formatPrice';
import getPaymentMethodLabel from '../getPaymentMethodLabel';
import getSellerPortalUrl from '../../lib/getSellerPortalUrl';
import getCouponDiscount from '../../lib/getCouponDiscount';

const VARIABLE_NODE_TYPE = 'variable';
const CONDITION_NODE_TYPE = 'condition';

function evaluateCondition(condition, object, seller, page) {
  const { variable, operator, value } = condition;
  let fieldValue;

  // Get the field value using the same logic as getValue
  try {
    if (variable.id.startsWith('field_')) {
      const fieldId = variable.id.replace('field_', '');
      const field = object.fields.find(field => field.fieldId === fieldId);
      fieldValue = field?.value;
    } else {
      // Handle all the special cases from getValue
      if (variable.id === 'variant_breakdown') {
        fieldValue = object.variants
          ?.map(variant => {
            if (variant.selectedOptions && variant.selectedOptions.length > 0) {
              return variant.selectedOptions
                .map(option => `${option.quantity}x ${option.optionName}`)
                .join(', ');
            }
            return `1x ${variant.selectedOptionName}`;
          })
          .join(', ');
      } else if (variable.id.startsWith('variant_') && !variable.id.endsWith('_sku')) {
        const variantId = variable.id.replace('variant_', '');
        const variant = object.variants.find(variant => variant.variantId === variantId);
        if (variant) {
          if (variant.selectedOptions && variant.selectedOptions.length > 0) {
            fieldValue = variant.selectedOptions
              .map(option => `${option.quantity}x ${option.optionName}`)
              .join(', ');
          } else {
            fieldValue = variant.selectedOptionName;
          }
        }
      } else if (variable.id.startsWith('variant_') && variable.id.endsWith('_sku')) {
        const variantId = variable.id.replace('variant_', '').replace('_sku', '');
        const variant = object.variants.find(variant => variant.variantId === variantId);
        fieldValue = variant?.sku;
      } else if (variable.id === 'price') {
        fieldValue = object.amount;
      } else if (variable.id === 'amountDue') {
        fieldValue = object.amountDue;
      } else if (variable.id === 'amountPaid') {
        fieldValue = object.amountPaid;
      } else if (variable.id === 'coupon_code') {
        fieldValue = object.coupon?.code;
      } else if (variable.id === 'product_title') {
        fieldValue = page?.productTitle;
      } else if (variable.id === 'form_title') {
        fieldValue = page?.title;
      } else if (variable.id === 'sku') {
        fieldValue = page?.product?.sku;
      } else if (variable.id === 'license_key') {
        fieldValue = object.licenseKey?.key;
      } else if (variable.id === 'status') {
        fieldValue = object.status;
      } else if (variable.id === 'payment_method') {
        fieldValue = object.paymentMethod;
      } else if (variable.id === 'event_name') {
        fieldValue = page?.title;
      } else if (variable.id === 'event_location') {
        fieldValue = page?.eventDetails?.location;
      } else {
        fieldValue = object[variable.id];
      }
    }
  } catch (e) {
    return false;
  }

  // If we couldn't find a value, condition is false
  if (fieldValue === undefined || fieldValue === null) {
    return false;
  }

  // Handle numeric comparisons
  if (!isNaN(fieldValue) && !isNaN(value)) {
    const numFieldValue = Number(fieldValue);
    const numValue = Number(value);

    switch (operator) {
      case 'equals':
        return numFieldValue === numValue;
      case 'not_equals':
        return numFieldValue !== numValue;
      case 'greater_than':
        return numFieldValue > numValue;
      case 'less_than':
        return numFieldValue < numValue;
      case 'greater_than_or_equal':
        return numFieldValue >= numValue;
      case 'less_than_or_equal':
        return numFieldValue <= numValue;
    }
  }

  // Handle boolean values
  if (typeof fieldValue === 'boolean' || fieldValue === 'true' || fieldValue === 'false') {
    const boolFieldValue = String(fieldValue).toLowerCase() === 'true';
    const boolValue = String(value).toLowerCase() === 'true';

    switch (operator) {
      case 'equals':
        return boolFieldValue === boolValue;
      case 'not_equals':
        return boolFieldValue !== boolValue;
    }
  }

  // Convert both values to strings for string comparisons
  const stringValue = String(fieldValue).toLowerCase();
  const compareValue = String(value).toLowerCase();

  switch (operator) {
    case 'equals':
      return stringValue === compareValue;
    case 'not_equals':
      return stringValue !== compareValue;
    case 'contains':
      return stringValue.includes(compareValue);
    case 'not_contains':
      return !stringValue.includes(compareValue);
    case 'starts_with':
      return stringValue.startsWith(compareValue);
    case 'ends_with':
      return stringValue.endsWith(compareValue);
    default:
      return false;
  }
}

function serialize(value, options = {}) {
  if (!value) return '';
  return serializeNode(value, options);
}

function serializeNode(node, options = {}) {
  const { delimiter = '\n' } = options;

  if (!node) return '';

  // Handle text nodes
  if (typeof node.text !== 'undefined') {
    return node.text;
  }

  // Handle variable nodes
  if (node.type === VARIABLE_NODE_TYPE) {
    return node.children?.[0]?.text || '';
  }

  // Handle element nodes with children
  if (node.children) {
    const childText = node.children.map(child => serializeNode(child, options)).join('');

    // Only add newlines for block elements
    if (node.type === 'paragraph') {
      return childText + delimiter;
    }
    return childText;
  }

  // Handle array of nodes - add delimiter after each sequence of non-paragraph nodes
  if (Array.isArray(node)) {
    let result = '';
    let inParagraph = false;

    node.forEach((child, index) => {
      const isLastNode = index === node.length - 1;
      const nextNode = !isLastNode ? node[index + 1] : null;

      if (child.type === 'paragraph') {
        result += serializeNode(child, options);
        inParagraph = true;
      } else {
        result += serializeNode(child, options);
        inParagraph = false;

        // Add delimiter if we're transitioning from non-paragraph to paragraph
        if (nextNode && nextNode.type === 'paragraph' && !inParagraph) {
          result += delimiter;
        }
      }
    });

    return result;
  }

  return '';
}

export default function getConfirmationWithVariables(content, object, seller, page) {
  function getValue(id) {
    let value;
    try {
      value = object[id];
    } catch (e) {
      // no-op
    }

    if (!value) {
      if (id.startsWith('field_')) {
        const fieldId = id.replace('field_', '');
        const field = object.fields.find(field => field.fieldId === fieldId);

        if (field) {
          value = field.value;
        }
      }

      if (id === 'variant_breakdown') {
        value = `<ul class="list-none p-0 m-0 text-center normal-case whitespace-normal">
          ${object.variants
            ?.map(variant => {
              let variantString = `<li class="mb-2"><span class="font-medium">${variant.name}</span>`;

              variantString += `<ul class="list-none p-0 m-0 normal-case whitespace-normal">`;

              if (variant.selectedOptions && variant.selectedOptions.length > 0) {
                // quantity selection or multiple selection
                variantString += variant.selectedOptions
                  .map(
                    option =>
                      `<li class="">${option.quantity} x ${option.optionName}${
                        option.additionalChargeAmount
                          ? ` (${formatPrice({
                              amount: option.additionalChargeAmount * option.quantity,
                              currency: object.currency,
                              locale: object.locale,
                              style: 'currency',
                            })})`
                          : ''
                      }</li>`,
                  )
                  .join('');
              } else {
                // single selection
                variantString += `<li class="">1 x ${variant.selectedOptionName}${
                  variant.additionalChargeAmount
                    ? ` (${formatPrice({
                        amount: variant.additionalChargeAmount,
                        currency: object.currency,
                        locale: object.locale,
                        style: 'currency',
                      })})`
                    : ''
                }</li>`;
              }

              variantString += '</ul></li>';
              return variantString;
            })
            .join('')}
        </ul>`;
      }

      if (id.startsWith('variant_') && !id.endsWith('_sku')) {
        const variantId = id.replace('variant_', '');
        const variant = object.variants.find(variant => variant.variantId === variantId);

        if (variant) {
          if (variant.selectedOptions && variant.selectedOptions.length > 0) {
            value = variant.selectedOptions
              .map(
                option =>
                  `${option.quantity}x ${option.optionName} ${
                    option.additionalChargeAmount
                      ? `(${formatPrice({
                          amount: option.additionalChargeAmount * option.quantity,
                          currency: object.currency,
                          locale: object.locale,
                          style: 'currency',
                        })})`
                      : ''
                  }`,
              )
              .join(', ');
          } else {
            value = `${variant.selectedOptionName} ${
              variant.additionalChargeAmount
                ? `(${formatPrice({
                    amount: variant.additionalChargeAmount,
                    currency: object.currency,
                    locale: object.locale,
                    style: 'currency',
                  })})`
                : ''
            }`;
          }
        }
      }

      if (id.startsWith('variant_') && id.endsWith('_sku')) {
        const variantId = id.replace('variant_', '').replace('_sku', '');
        const variant = object.variants.find(variant => variant.variantId === variantId);

        if (variant) {
          value = variant.sku;
        }
      }
    }

    if (id === 'price') {
      value = formatPrice({
        amount: object.amount,
        currency: object.currency,
        locale: object.locale,
        style: 'currency',
      });
    }

    if (id === 'amountDue') {
      value = formatPrice({
        amount: object.amountDue,
        currency: object.currency,
        locale: object.locale,
        style: 'currency',
      });
    }

    if (id === 'amountPaid') {
      value = formatPrice({
        amount: object.amountPaid,
        currency: object.currency,
        locale: object.locale,
        style: 'currency',
      });
    }

    if (id === 'customer_portal_link') {
      const portalUrl = getSellerPortalUrl(seller);
      value = `<a href="${portalUrl}" target="_blank" style="text-decoration: underline;">${portalUrl}</a>`;
    }

    if (id === 'coupon_code') {
      if (object.coupon) {
        value = object.coupon?.code;
      }
    }

    if (id === 'coupon_discount') {
      if (object.coupon && object.coupon.discount) {
        value = formatPrice({
          amount: object.coupon?.discount,
          currency: object.currency,
          locale: object.locale,
          style: 'currency',
        });
      } else if (object.coupon) {
        if (object.coupon.amountOff) {
          value = formatPrice({
            amount: object.coupon.amountOff,
            currency: object.currency,
            locale: object.locale,
            style: 'currency',
          });
        } else if (object.coupon.percentOff) {
          const amountToBeDiscounted = getAmountToBeDiscounted({
            subscriptionAmount: object.amount,
            fees: object.fees,
          });
          const couponDiscount = getCouponDiscount(amountToBeDiscounted, object.coupon);
          const setupFeeDiscountAmount = object.setupFeeDiscountAmount || 0;
          const variantsDiscountAmount =
            object.variants?.reduce((acc, variant) => {
              if (variant.discountAmount) {
                return acc + variant.discountAmount * (variant.selectedOptionQuantity || 1);
              }

              if (variant.selectedOptions) {
                return (
                  acc +
                  variant.selectedOptions.reduce((acc, option) => {
                    if (option.discountAmount) {
                      return acc + option.discountAmount * (option.quantity || 1);
                    }

                    return acc;
                  }, 0)
                );
              }

              return acc;
            }, 0) || 0;

          value = formatPrice({
            amount: couponDiscount + setupFeeDiscountAmount + variantsDiscountAmount,
            currency: object.currency,
            locale: object.locale,
            style: 'currency',
          });
        }
      }
    }

    if (id === 'product_title') {
      value = page?.productTitle;
    }

    if (id === 'form_title') {
      value = page?.title;
    }

    if (id === 'sku') {
      value = page?.product?.sku;
    }

    if (id == 'license_key') {
      value = object.licenseKey?.key;
    }

    if (id === 'createdAt') {
      value = format(new Date(object.createdAt), 'd MMM, yyyy, HH:mm');
    }

    if (id === 'currentPeriodEnd') {
      value = format(new Date(object.currentPeriodEnd), 'd MMM, yyyy, HH:mm');
    }

    if (id === 'status') {
      value = upperFirst(object.status);
    }

    if (id === 'payment_method') {
      value = getPaymentMethodLabel(object.paymentMethod);
    }

    if (id === 'event_name') {
      value = page?.title;
    }

    if (id === 'event_date') {
      if (page?.eventDetails?.startDate && page?.eventDetails?.endDate) {
        value = `${format(new Date(page?.eventDetails?.startDate), 'd MMM, yyyy')} - ${format(
          new Date(page?.eventDetails?.endDate),
          'd MMM, yyyy',
        )}`;
      } else if (page?.eventDetails?.startDate) {
        value = format(new Date(page?.eventDetails?.startDate), 'd MMM, yyyy');
      }
    }

    if (id === 'event_location') {
      if (page?.eventDetails?.mapsLink && page?.eventDetails?.location) {
        value = `<a href="${page?.eventDetails?.mapsLink}" target="_blank" style="text-decoration: underline;">${page?.eventDetails?.location}</a>`;
      } else if (page?.eventDetails?.location) {
        value = page?.eventDetails?.location;
      }
    }

    if (id === 'event_tickets') {
      if (object.tickets) {
        const tickets = object.tickets.reduce((acc, ticket) => {
          acc[ticket.name] = ticket.quantity;
          return acc;
        }, {});

        value = Object.entries(tickets)
          .map(([name, quantity], index) => {
            // each item should have a break except the last one
            if (index !== Object.keys(tickets).length - 1) {
              return `${quantity}x ${name} <br />`;
            }

            return `${quantity}x ${name}`;
          })
          .join('');
      }
    }

    return value;
  }

  function replaceVariables(content) {
    function replaceNode(node) {
      // Handle variable elements
      if (node.type === VARIABLE_NODE_TYPE) {
        const value = getValue(node.data.id);

        // Update the text node within the variable element
        if (node.children && node.children[0]) {
          node.children[0].text = value || '';
        }

        return node;
      }

      // Handle condition elements
      if (node.type === CONDITION_NODE_TYPE) {
        // Use evaluateCondition to determine if we should show the content
        const isTrue = evaluateCondition(node.data, object, seller, page);

        if (!isTrue) {
          // Return an empty paragraph if condition is false
          return {
            type: 'paragraph',
            children: [{ text: '' }],
          };
        }

        // If condition is true, process its children for variables
        if (node.children) {
          return {
            ...node,
            children: node.children.map(child => replaceNode(child)),
          };
        }
      }

      // Recursively process children if they exist
      if (node.children) {
        return {
          ...node,
          children: node.children.map(child => replaceNode(child)),
        };
      }

      return node;
    }

    // Handle both array and object formats for the content
    if (Array.isArray(content)) {
      return content.map(node => replaceNode(node));
    }

    return replaceNode(content);
  }

  function hasTextInNodes(node) {
    if (typeof node.text !== 'undefined') {
      return !!node.text;
    }

    if (node.children && node.children.length > 0) {
      return node.children.some(hasTextInNodes);
    }

    return false;
  }

  function hasVariableNode(node) {
    if (node.type === 'variable') {
      return true;
    }

    if (node.children && node.children.length > 0) {
      return node.children.some(hasVariableNode);
    }

    return false;
  }

  const replaced = replaceVariables(content);

  // Filter out paragraph nodes that contain no more than one variable node with empty text
  const filteredNodes = Array.isArray(replaced) ? replaced : [replaced];
  const filteredContent = filteredNodes.filter(node => {
    if (node.type === 'paragraph') {
      if (hasVariableNode(node) && !hasTextInNodes(node)) {
        return false;
      }
      return true;
    }
    return true;
  });

  // Make sure we serialize the content before passing it to linkifyHtml
  const serialized = serialize(filteredContent);

  return linkifyHtml(serialized, {
    ignoreTags: ['script'],
  });
}

function getAmountToBeDiscounted({ subscriptionAmount = 0, fees = [] }) {
  // subscriptionAmount is total recurring amount including fees, before coupon discount
  // fees aren't discounted so need to be subtracted
  // setup fee may be added if coupon applies to setup fee
  let amount = subscriptionAmount;

  if (fees.length > 0) {
    amount -= fees.reduce((acc, fee) => acc + fee.amount, 0);
  }

  return amount;
}
